import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { supabase, hasDurableStoredSession } from '@/integrations/supabase/client';
import { PageHeaderSkeleton, CardGridSkeleton } from '@/components/ui/skeletons';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireDeveloper?: boolean;
}

// A null live-session means one of two very different things:
//   1. Genuinely logged out — nothing is persisted → send to /auth.
//   2. Logged in, but the session hasn't rehydrated/refreshed yet — common on
//      an iOS PWA cold-reopen where the token refresh is still waiting on a
//      network round-trip. A durable session IS persisted; ejecting to /auth
//      here is the long-standing "PWA logs me out on reopen" bug.
// We tell them apart by peeking durable storage, and on case 2 we try an
// explicit refresh and only redirect on a HARD auth failure (invalid/expired
// refresh token), never on a transient network error.
function isHardAuthFailure(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { status?: number; code?: string; name?: string };
  // 400/401 from the token endpoint = the refresh token is genuinely no good.
  if (e.status === 400 || e.status === 401) return true;
  if (e.name === 'AuthApiError') return true;
  if (e.code === 'refresh_token_not_found' || e.code === 'refresh_token_already_used') return true;
  return false;
}

export const ProtectedRoute = ({ children, requireDeveloper = false }: ProtectedRouteProps) => {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isDeveloper, setIsDeveloper] = useState(false);
  // True once we've decided the user is genuinely logged out (redirect). Kept
  // separate from "still resolving" so a transient null never flips us to the
  // redirect state prematurely.
  const [loggedOut, setLoggedOut] = useState(false);
  // Mirror isAuthenticated into a ref so the retry handlers inside the effect
  // can read the latest value without re-subscribing.
  const isAuthenticatedRef = useRef(false);
  isAuthenticatedRef.current = isAuthenticated;

  useEffect(() => {
    let active = true;
    const refreshingRef = { current: false };
    // Which user id we've already resolved developer status for — so a token
    // refresh / tab-focus event doesn't re-run the role query (and can't flip
    // an established isDeveloper back to false).
    const devResolvedForRef = { current: null as string | null };

    const markAuthed = async (session: import('@supabase/supabase-js').Session) => {
      if (!active) return;
      setIsAuthenticated(true);
      setLoggedOut(false);
      // Developer status can't change on a token refresh / tab-focus event, so
      // resolve it ONCE per user. Re-querying on every auth tick meant a single
      // transient null (network / RLS hiccup) flipped isDeveloper→false and the
      // gate below ejected the whole /dashboard mid-work, remounting it on the
      // next good event — the "flick and reset out of nowhere" bug. Never
      // downgrade an already-granted developer on a transient failure.
      if (requireDeveloper && devResolvedForRef.current !== session.user.id) {
        try {
          const { data: roles } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', session.user.id)
            .eq('role', 'developer')
            .maybeSingle();
          if (active) {
            setIsDeveloper(!!roles);
            devResolvedForRef.current = session.user.id;
          }
        } catch {
          // Transient failure — keep the prior determination and retry on the
          // next event rather than downgrading (and ejecting) the user.
        }
      }
      if (active) setIsLoading(false);
    };

    const redirectToLogin = () => {
      if (!active) return;
      setIsAuthenticated(false);
      setIsDeveloper(false);
      setLoggedOut(true);
      setIsLoading(false);
    };

    // Try to recover a session that's persisted but not currently live.
    const attemptRecover = async () => {
      if (refreshingRef.current) return;
      refreshingRef.current = true;
      try {
        const { data, error } = await supabase.auth.refreshSession();
        if (!active) return;
        if (data?.session?.user) {
          await markAuthed(data.session);
          return;
        }
        if (isHardAuthFailure(error)) {
          // Refresh token is genuinely dead — real logout.
          redirectToLogin();
          return;
        }
        // Soft failure (offline / network). Stay in the loading state and let
        // the retry hooks below re-attempt when connectivity returns. We do
        // NOT redirect — that's exactly the eject-on-transient-hiccup bug.
      } catch {
        // Network throw — treat as soft; keep waiting.
      } finally {
        refreshingRef.current = false;
      }
    };

    const evaluate = async (
      event: string,
      session: import('@supabase/supabase-js').Session | null,
    ) => {
      if (!active) return;
      if (session?.user) {
        await markAuthed(session);
        return;
      }
      // session is null …
      if (event === 'SIGNED_OUT') {
        redirectToLogin();
        return;
      }
      const persisted = await hasDurableStoredSession();
      if (!persisted) {
        // Nothing in durable storage → genuinely logged out.
        redirectToLogin();
        return;
      }
      // We have a persisted session but no live one yet → recover, don't eject.
      void attemptRecover();
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      void evaluate(event, session);
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      void evaluate('INITIAL_SESSION', session);
    });

    // Retry recovery when connectivity or focus returns — turns an offline
    // cold-reopen from a logout into a brief skeleton that resolves itself.
    const retry = () => {
      if (!active || isAuthenticatedRef.current) return;
      void hasDurableStoredSession().then((persisted) => {
        if (persisted) void attemptRecover();
      });
    };
    window.addEventListener('online', retry);
    document.addEventListener('visibilitychange', retry);

    return () => {
      active = false;
      subscription.unsubscribe();
      window.removeEventListener('online', retry);
      document.removeEventListener('visibilitychange', retry);
    };
  }, [requireDeveloper]);

  // While we still believe a session may exist (loading and not yet declared
  // logged out), show the skeleton rather than redirecting.
  if (isLoading && !loggedOut) {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="max-w-5xl mx-auto space-y-6">
          <PageHeaderSkeleton actions={1} />
          <CardGridSkeleton count={6} cols={3} />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    const returnTo = location.pathname + location.search;
    return <Navigate to={`/auth?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }

  if (requireDeveloper && !isDeveloper) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};
